import React from 'react'
import { Dialog, AppBar, Toolbar, IconButton, Typography, Box, Button } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckIcon from '@mui/icons-material/Check'

export interface MobilePickerOption {
  value: string
  label: string
  /** 副標籤（顯示於主標下方） */
  subLabel?: string
}

interface MobilePickerProps {
  open: boolean
  title: string
  options: MobilePickerOption[]
  value: string
  onChange: (value: string) => void
  onClose: () => void
  /** 「全部 / 空值」顯示文字。若設定，會額外顯示一個「清除選擇」選項 */
  clearLabel?: string
  /** 找不到選項時的提示 */
  emptyHint?: string
}

/**
 * 全螢幕大按鈕選擇器 — 給卡車司機在 LINE@ 操作用。
 *
 * 對齊 LINE@ 其他整頁體驗（fullScreen Dialog + 棕色 AppBar），列表每筆 60px 高、
 * 大字、選中時棕色高亮 + 勾選圖示。
 */
const MobilePicker: React.FC<MobilePickerProps> = ({
  open, title, options, value, onChange, onClose,
  clearLabel, emptyHint = '無可選項',
}) => {
  const handleSelect = (v: string) => {
    onChange(v)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen TransitionProps={{ timeout: 200 }}>
      <AppBar position="sticky" sx={{ backgroundColor: '#6B705C' }} elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="返回">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            {title}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, overflowY: 'auto', backgroundColor: '#fafafa', p: 1 }}>
        {clearLabel && (
          <Button
            fullWidth
            onClick={() => handleSelect('')}
            sx={{
              backgroundColor: value === '' ? '#fff8ec' : 'white',
              color: '#666',
              border: value === '' ? '2px solid #6B705C' : '1px solid #e0e0e0',
              borderRadius: 2,
              py: 2,
              mb: 1,
              fontSize: '1rem',
              justifyContent: 'space-between',
              textAlign: 'left',
              px: 2,
              textTransform: 'none',
            }}
          >
            <span>{clearLabel}</span>
            {value === '' && <CheckIcon sx={{ color: '#6B705C' }} />}
          </Button>
        )}
        {options.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: '#999' }}>{emptyHint}</Box>
        )}
        {options.map((opt) => {
          const selected = opt.value === value
          return (
            <Button
              key={opt.value}
              fullWidth
              onClick={() => handleSelect(opt.value)}
              sx={{
                backgroundColor: selected ? '#fff8ec' : 'white',
                color: '#333',
                border: selected ? '2px solid #6B705C' : '1px solid #e0e0e0',
                borderRadius: 2,
                py: 2,
                mb: 1,
                fontSize: '1rem',
                justifyContent: 'space-between',
                textAlign: 'left',
                px: 2,
                textTransform: 'none',
                fontWeight: selected ? 600 : 400,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{opt.label}</span>
                {opt.subLabel && (
                  <span style={{ fontSize: '0.8rem', color: '#888', fontWeight: 400 }}>
                    {opt.subLabel}
                  </span>
                )}
              </Box>
              {selected && <CheckIcon sx={{ color: '#6B705C' }} />}
            </Button>
          )
        })}
      </Box>
    </Dialog>
  )
}

export default MobilePicker
